/**
 * External dependencies
 */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Box, Stack, Text } from '@towersystems/towersystems-design-system';
import { size, map } from 'lodash';

/**
 * Internal dependencies
 */
import Modal from 'components/modal';
import { createCloseModalAction } from 'state/ui/actions';
import { isModalVisible } from 'state/ui/selectors';
import { getResourceConnectId } from 'state/ui/sync-resource-connect-modal/selectors';
import { requestResourceConnect, syncResourceConnect } from 'state/resource-connects/actions';
import Skeleton from 'react-loading-skeleton';
import Tag from 'components/tag';
import ButtonGroup from 'components/button-group';
import Button from 'components/button';
import JumpingDots from 'components/jumping-dots';
import DateTime from 'components/date-time';

/**
 * Style dependencies
 */
import './styles.scss';

const SyncResourceConnectModal = ({ resourceConnectId, onClosed, isVisible, close, ...props }) => {
    const [requesting, setRequesting] = useState(true);
    const [syncing, setSyncing] = useState(false);

    const [resourceConnect, setResourceConnect] = useState(null);
    const [resultResourceConnect, setResultResourceConnect] = useState(null);

    useEffect(() => {
        if (resourceConnectId) {
            setRequesting(true);
            setSyncing(false);
            setResourceConnect(false);
            setResultResourceConnect(false);
            loadData(resourceConnectId);
        }
    }, [resourceConnectId, isVisible]);

    const loadData = async (id) => {
        try {
            setRequesting(true);
            let result = await props.requestResourceConnect(resourceConnectId);
            setResourceConnect(result);
            setRequesting(false);
        } catch (error) {
            console.log(error);
            close();
        }
    };

    const handleOnClosed = () => {
        onClosed && onClosed();
    };

    const syncResource = async () => {
        try {
            setSyncing(true);
            setResultResourceConnect(false);
            let result = await props.syncResourceConnect(resourceConnectId);
            setResultResourceConnect(result);
        } catch (error) {}
        setSyncing(false);
    };

    if (!isVisible || !resourceConnectId) {
        return null;
    }

    return (
        <Modal
            onClosed={handleOnClosed}
            header={false}
            className='create-connection-modal'
            close={close}
            isVisible={isVisible}
        >
            <Box
                padding='xlarge'
                display='flex'
                alignItems='center'
                justifyContent='center'
                style={{ height: '100%', width: '100%' }}
            >
                {!resultResourceConnect ? (
                    <Stack space='large'>
                        <Text align='center' size='large'>
                            {requesting ? (
                                <Skeleton height={30} />
                            ) : syncing ? (
                                <div>
                                    Your resource is syncing <JumpingDots />
                                </div>
                            ) : (
                                'Start syncing your resource?'
                            )}
                        </Text>
                        <Box>
                            {requesting ? (
                                <Skeleton height={30} />
                            ) : syncing ? (
                                <Text size='xsmall' align='center'>
                                    Please wait, this may take a few moments.
                                </Text>
                            ) : (
                                <Text size='xsmall' align='center'>
                                    <Tag>{resourceConnect.resource_type}</Tag>{' '}
                                    {resourceConnect.last_synced && resourceConnect.external_id
                                        ? <DateTime date={resourceConnect.last_synced} format='DD/MM/YYYY HH:mm:ss' text='last synced '/>
                                        : ' is yet to be synced.'}
                                </Text>
                            )}
                        </Box>
                        {requesting ? (
                            <Skeleton height={30} />
                        ) : (
                            <ButtonGroup alignItems='center' display='flex' justifyContent='center' marginTop='large'>
                                <Button disabled={syncing} onClick={close} style={{ width: 150 }}>
                                    Cancel
                                </Button>
                                <Button disabled={syncing} onClick={syncResource} style={{ width: 150 }} primary>
                                    {syncing ? (
                                        <div>
                                            Syncing <JumpingDots />
                                        </div>
                                    ) : (
                                        'Start'
                                    )}
                                </Button>
                            </ButtonGroup>
                        )}
                    </Stack>
                ) : size(resultResourceConnect.errors) >= 1 ? (
                    <Stack space='large'>
                        <Text size='large' align='center'>
                            Your resource has failed to synced.
                        </Text>
                        {map(resultResourceConnect.errors, (error) => {
                            return (
                                <Text size='small' align='center'>
                                    <Tag error>error</Tag> {error.message}
                                </Text>
                            );
                        })}
                        {map(resultResourceConnect.warnings, (error) => {
                            return (
                                <Text size='small' align='center'>
                                    <Tag warning>warning</Tag> {error.message}
                                </Text>
                            );
                        })}
                        <ButtonGroup alignItems='center' display='flex' justifyContent='center' marginTop='large'>
                            <Button disabled={syncing} onClick={close} style={{ width: 150 }}>
                                Done
                            </Button>
                            <Button onClick={syncResource} style={{ width: 150 }} primary>
                                Try again
                            </Button>
                        </ButtonGroup>
                    </Stack>
                ) : (
                    <Stack space='large'>
                        <Text size='large' align='center'>
                            Your resource has been synced sucessfully.
                        </Text>
                        {map(resultResourceConnect.warnings, (error) => {
                            return (
                                <Text size='small' align='center'>
                                    <Tag warning>warning</Tag> {error.message}
                                </Text>
                            );
                        })}
                        <ButtonGroup alignItems='center' display='flex' justifyContent='center' marginTop='large'>
                            <Button disabled={syncing} onClick={close} style={{ width: 150 }}>
                                Done
                            </Button>
                        </ButtonGroup>
                    </Stack>
                )}
            </Box>
        </Modal>
    );
};

export default connect(
    (state) => ({
        isVisible: isModalVisible('SyncResourceConnectModal')(state),
        resourceConnectId: getResourceConnectId(state),
    }),
    {
        close: createCloseModalAction('SyncResourceConnectModal'),
        requestResourceConnect,
        syncResourceConnect,
    },
)(SyncResourceConnectModal);
